import React from "react";
import { SHARED_RECORD_TYPES, SHARING_LABELS } from "utils/sharing";
import { useGetNetworkByIdQuery } from "apis/network";
import { formatDateTime } from "utils/helperFunctions";

const statusStyles = {
  active: "bg-green-100 text-green-800",
  suspended: "bg-red-100 text-red-800",
  inactive: "bg-lightgray text-darkergray",
};

const renderJson = (value, emptyText = "No data") => {
  if (
    value === null ||
    value === undefined ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === "object" &&
      !Array.isArray(value) &&
      Object.keys(value).length === 0)
  ) {
    return <p className="text-darkgray italic">{emptyText}</p>;
  }

  return (
    <pre className="bg-lightgray rounded-lg p-4 overflow-x-auto text-xs text-black whitespace-pre-wrap break-all">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
};

const ValueRow = ({ label, value, href = null }) => (
  <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
    <span className="text-darkgray">{label}</span>
    {href && value ? (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 break-all text-left sm:text-right"
      >
        {value}
      </a>
    ) : (
      <span className="text-black break-all text-left sm:text-right">
        {value || "-"}
      </span>
    )}
  </div>
);

const Section = ({ title, children }) => (
  <section className="space-y-3">
    <h5 className="text-base font-semibold text-black">{title}</h5>
    {children}
  </section>
);

export default function OrganizationView({ organization }) {
  // The organization endpoint returns the group RAW, so network-owned fields
  // such as slug, SMS and brand logos come from the network itself. The
  // assistant may be overridden at organization level.
  const { data: network } = useGetNetworkByIdQuery(
    organization?.networkId?._id || organization?.networkId,
  );

  if (!organization) return null;

  // Group-owned: ad-copy disclaimer and optional assistant override. An office
  // may set a more specific name.
  const branding = { ...(network?.branding || {}), ...(organization.branding || {}) };
  const trustAccount = organization.trustAccount || {};
  const toneOfVoice = organization.toneOfVoice || {};
  const smsIntegration = network?.integrations?.sms || {};
  // Organizations created before `smsProvider` existed only carry Intelli SMS
  // credentials, so infer the provider when the field is missing.
  const smsProvider =
    smsIntegration.smsProvider ||
    (smsIntegration.twilioAccountSid || smsIntegration.twilioAuthToken
      ? "twilio"
      : "intellisms");
  const isTwilioProvider = smsProvider === "twilio";
  const smsProviderLabel = isTwilioProvider ? "Twilio" : "Intelli SMS";
  const assistant = organization.aiAssistant?.name
    ? organization.aiAssistant
    : network?.aiAssistant;

  return (
    <div className="bg-white space-y-6">
      <div className="bg-lightgray p-6 rounded-lg">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <div className="text-lg font-semibold text-black">
              {organization.name}
            </div>
            <p className="text-darkgray">
              {network?.name || "—"} · slug: {network?.slug || "-"}
            </p>
          </div>
          <span
            className={`inline-flex w-fit items-center px-3 py-1 rounded-full text-xs font-medium capitalize ${
              statusStyles[organization.status] || statusStyles.active
            }`}
          >
            {organization.status || "active"}
          </span>
        </div>
      </div>

      <Section title="Overview">
        <div className="bg-lightgray rounded-lg p-4 space-y-3">
          <ValueRow
            label="AI Assistant Name"
            value={assistant?.name}
          />
          <ValueRow
            label="Trust Account Enabled"
            value={trustAccount.isEnabled ? "Yes" : "No"}
          />
          <ValueRow
            label="Use Beleef Trust Account"
            value={trustAccount.useBeleefTrustAccount ? "Yes" : "No"}
          />
          <ValueRow
            label="Brand Assets"
            value={String(organization.brandAssets?.length || 0)}
          />
          <ValueRow
            label="Created"
            value={formatDateTime(organization.createdAt)}
          />
          <ValueRow
            label="Updated"
            value={formatDateTime(organization.updatedAt)}
          />
        </div>
      </Section>

      <Section title="Sharing Across Offices">
        <div className="bg-lightgray rounded-lg p-4 space-y-3">
          {SHARED_RECORD_TYPES.map((recordType) => (
            <ValueRow
              key={recordType.key}
              label={recordType.name}
              // Inherit is not Private — it takes whatever a level above
              // decided, which may well be open.
              value={
                SHARING_LABELS[organization.sharing?.[recordType.key]] ||
                SHARING_LABELS.inherit
              }
            />
          ))}
        </div>
      </Section>

      <Section title="Branding">
        <div className="bg-lightgray rounded-lg p-4 space-y-3">
          <ValueRow
            label="Primary Logo"
            value={branding.logos?.primary}
            href={branding.logos?.primary}
          />
          <ValueRow
            label="Secondary Logo"
            value={branding.logos?.secondary}
            href={branding.logos?.secondary}
          />
          <ValueRow label="Primary Color" value={branding.colors?.primary} />
          <ValueRow
            label="Secondary Color"
            value={branding.colors?.secondary}
          />
          <ValueRow label="Accent Color" value={branding.colors?.accent} />
          <ValueRow label="Heading Font" value={branding.fonts?.heading} />
          <ValueRow label="Body Font" value={branding.fonts?.body} />
          <ValueRow
            label="Privacy Policy"
            value={branding.privacyPolicyUrl}
            href={branding.privacyPolicyUrl}
          />
          <ValueRow
            label="Facebook"
            value={branding.socialLinks?.facebook}
            href={branding.socialLinks?.facebook}
          />
          <ValueRow
            label="Instagram"
            value={branding.socialLinks?.instagram}
            href={branding.socialLinks?.instagram}
          />
          <ValueRow
            label="LinkedIn"
            value={branding.socialLinks?.linkedin}
            href={branding.socialLinks?.linkedin}
          />
          <ValueRow
            label="YouTube"
            value={branding.socialLinks?.youtube}
            href={branding.socialLinks?.youtube}
          />
        </div>
      </Section>

      <Section title="Operational Emails">
        <div className="bg-lightgray rounded-lg p-4 space-y-3">
          <ValueRow
            label="Settlements"
            value={organization.operationalEmails?.settlements}
          />
          <ValueRow
            label="Concierge"
            value={organization.operationalEmails?.concierge}
          />
          <ValueRow
            label="Key Events"
            value={organization.operationalEmails?.keyEvents}
          />
          <ValueRow
            label="Accounts"
            value={organization.operationalEmails?.accounts}
          />
          <ValueRow
            label="Admin"
            value={organization.operationalEmails?.admin}
          />
        </div>
      </Section>

      <Section title="Trust Account">
        <div className="bg-lightgray rounded-lg p-4 space-y-3">
          <ValueRow label="Name" value={trustAccount.name} />
          <ValueRow label="BSB" value={trustAccount.bsb} />
          <ValueRow label="Account Number" value={trustAccount.accountNumber} />
          <ValueRow label="ABN" value={trustAccount.abn} />
          <ValueRow label="GST" value={trustAccount.gst} />
          <ValueRow label="Address" value={trustAccount.address} />
          <ValueRow label="Mobile" value={trustAccount.mobile} />
          <ValueRow label="Email" value={trustAccount.email} />
          <ValueRow label="License Number" value={trustAccount.licenseNumber} />
          <ValueRow label="Signatory Name" value={trustAccount.signatoryName} />
          <ValueRow label="Signature" value={trustAccount.signature} />
        </div>
      </Section>

      <Section title="SMS Integration">
        <div className="bg-lightgray rounded-lg p-4 space-y-3">
          <ValueRow label="Provider" value={smsProviderLabel} />
          {isTwilioProvider ? (
            <>
              <ValueRow
                label="Account SID"
                value={smsIntegration.twilioAccountSid}
              />
              <ValueRow
                label="Auth Token"
                value={smsIntegration.twilioAuthToken}
              />
            </>
          ) : (
            <>
              <ValueRow label="Username" value={smsIntegration.username} />
              <ValueRow label="Password" value={smsIntegration.password} />
            </>
          )}
          <ValueRow
            label="Alpha Sender ID"
            value={smsIntegration.alphaSenderId}
          />
        </div>
      </Section>

      <Section title="Compliance">
        <div className="space-y-3">
          <div className="bg-lightgray rounded-lg p-4 space-y-3">
            <ValueRow
              label="Restricted Days"
              value={organization.compliance?.restrictedDays?.join(", ") || "-"}
            />
          </div>
          {renderJson(organization.compliance?.rules, "No compliance rules")}
        </div>
      </Section>

      <Section title="Tone Of Voice">
        <div className="bg-lightgray rounded-lg p-4 space-y-3">
          <ValueRow label="Status" value={toneOfVoice.status} />
          <ValueRow
            label="Analyzed At"
            value={formatDateTime(toneOfVoice.analyzedAt)}
          />
          <ValueRow label="Letter Sample" value={toneOfVoice.samples?.letter} />
          <ValueRow label="SMS Sample" value={toneOfVoice.samples?.sms} />
          <ValueRow
            label="Mailcard Sample"
            value={toneOfVoice.samples?.mailcard}
          />
          <ValueRow
            label="Proposal Sample"
            value={toneOfVoice.samples?.proposal}
          />
        </div>
        {renderJson(toneOfVoice.sources, "No sources")}
        {renderJson(toneOfVoice.profile, "No profile")}
        {renderJson(toneOfVoice.manualAdjustment, "No manual adjustment")}
      </Section>

      <Section title="Templates">
        <div className="bg-lightgray rounded-lg p-4 space-y-3">
          <ValueRow
            label="Post List"
            value={organization.templates?.postList}
          />
        </div>
        {renderJson(organization.templates?.social, "No social templates")}
      </Section>

      <Section title="Brand Assets">
        {renderJson(organization.brandAssets, "No brand assets")}
      </Section>

      <Section title="Company Subscription">
        {renderJson(
          organization.companySubscription,
          "No company subscription",
        )}
      </Section>
    </div>
  );
}
